import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { apiErrorMessage } from '../utils/apiErrors';
import { SESSION_EXPIRED_MESSAGE } from '../utils/session';

export type ReauthReason = 'expired' | 'otherTabLogout';

interface SessionExpiredDialogProps {
  open: boolean;
  /** Az előző felhasználó e-mail címe — NEM szerkeszthető (más felhasználó itt nem léphet be). */
  email: string;
  /** A mentetlen források címkéi (pl. „Jelenléti ív"). */
  labels: string[];
  reason: ReauthReason;
  /** Újra-belépés; hibánál dob (a hibaszöveg az ablakban jelenik meg). */
  onLogin: (password: string) => Promise<void>;
  /** „Kijelentkezés" — a hívó kérdez rá (közös megerősítő ablak), és ő jelentkeztet ki. */
  onExit: () => void;
}

/**
 * Újra-belépő ablak lejárt munkamenetnél, MENTETLEN adat mellett.
 * [Web UX 2. csomag – Belépés és munkamenet, 2026-10-03]
 *
 * MIÉRT: a jelenléti ív mentésekor jövő 401 (`EventAttendance.tsx`, `POST /api/attendance/bulk`)
 * után a sima kijelentkeztetés ELDOBNÁ a 20 sornyi beírt pipát. Ez az ablak a felület FÖLÖTT nyílik,
 * a Dashboard alatta érintetlen marad; sikeres belépés után a felhasználó újra megnyomja a Mentés-t.
 * Esc-kel és háttér-kattintással NEM zárható (a döntés: belépés vagy tudatos kilépés).
 */
const SessionExpiredDialog: React.FC<SessionExpiredDialogProps> = ({
  open,
  email,
  labels,
  reason,
  onLogin,
  onExit,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Minden új megnyitás tiszta mezőkkel indul (a jelszó nem maradhat a memóriában).
  useEffect(() => {
    if (!open) {
      setPassword('');
      setShowPassword(false);
      setError('');
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError('Add meg a jelszavadat.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await onLogin(password);
    } catch (err) {
      setError(apiErrorMessage(err, 'Nem sikerült belépni.'));
      setSubmitting(false);
    }
  };

  const title = reason === 'otherTabLogout' ? 'Kijelentkeztél egy másik lapon' : 'Lejárt a bejelentkezésed';
  const lead =
    reason === 'otherTabLogout' ? 'Kijelentkeztél egy másik lapon, lépj be újra.' : SESSION_EXPIRED_MESSAGE;

  return (
    <Dialog
      open={open}
      // Esc és háttér-kattintás: NEM zár (szándékosan nincs `onClose`-ág, ami bezárna).
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      aria-labelledby="session-expired-dialog-title"
      data-testid="session-expired-dialog"
    >
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle id="session-expired-dialog-title">{title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {lead} A még nem mentett változásaid
            {labels.length ? ` (${labels.join(', ')})` : ''} megmaradnak; belépés után mentsd el őket újra.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }} data-testid="session-expired-dialog-error">
              {error}
            </Alert>
          )}
          <TextField
            margin="dense"
            fullWidth
            label="E-mail cím"
            value={email}
            // [validátor M7] A jelszókezelő ebből tudja, melyik fiók jelszavát kínálja fel.
            autoComplete="username"
            InputProps={{ readOnly: true }}
            disabled
            inputProps={{ 'data-testid': 'session-expired-email' }}
          />
          <TextField
            margin="dense"
            fullWidth
            autoFocus
            required
            label="Jelszó"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError('');
            }}
            disabled={submitting}
            inputProps={{ 'data-testid': 'session-expired-password' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Jelszó elrejtése' : 'Jelszó megjelenítése'}
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onExit} color="error" disabled={submitting} data-testid="session-expired-exit">
            Kijelentkezés
          </Button>
          {/* [validátor M4, 2026-10-03] A feliratok a belépő oldallal („Bejelentkezés", `Login.tsx`)
              és a Dashboard kilépő gombjával („Kijelentkezés") egyeznek. Az E2E-k az ablakot
              `data-testid`-del érik el (a szöveges `getByRole('button', { name: /Bejelentkezés/ })`
              szelektorok csak ablak NÉLKÜLI oldalon futnak). */}
          <Button type="submit" variant="contained" disabled={submitting} data-testid="session-expired-submit">
            {submitting ? <CircularProgress size={22} /> : 'Bejelentkezés'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default SessionExpiredDialog;
